import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type EstimateStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'cancelled';

interface ConflictData {
  sent_versions: Array<{
    id: string;
    version_number: number;
    version_type: string;
    created_at: string;
  }>;
  approved_versions: Array<{
    id: string;
    version_number: number;
    version_type: string;
    created_at: string;
  }>;
}

interface VersionCreationResult {
  version_id: string;
  version_number: number;
  version_type: 'estimate' | 'change_order';
  action: 'created_new_version' | 'updated_existing_draft';
}

interface StatusUpdateResult {
  success?: boolean;
  changed?: boolean;
  error?: string;
  old_main_version_id?: string;
  new_main_version_id?: string;
  main_version_changed?: boolean;
  version_count?: number;
}

interface PricingRefreshResult {
  hasChanges: boolean;
  action: 'no_changes' | 'direct_update' | 'new_version' | 'change_order';
  version?: VersionCreationResult;
}

export const useEstimateVersioningEnhanced = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Check for conflicts when upgrading status
  const checkStatusUpgradeConflicts = async (
    estimateId: string,
    newStatus: EstimateStatus,
    versionId?: string
  ): Promise<ConflictData> => {
    try {
      const { data, error } = await supabase.rpc('check_status_upgrade_conflicts', {
        p_estimate_id: estimateId,
        p_new_status: newStatus,
        p_version_id: versionId || null
      });

      if (error) throw error;
      return data as unknown as ConflictData;
    } catch (error) {
      console.error('Error checking status conflicts:', error);
      throw error;
    }
  };

  // Resolve status conflicts
  const resolveStatusConflicts = async (
    estimateId: string,
    conflictResolution: {
      sent_versions?: Array<{ id: string; action: 'cancel' | 'reject' | 'leave' }>;
      approved_versions?: Array<{ id: string; action: 'cancel' | 'reject' | 'leave' }>;
    }
  ): Promise<void> => {
    try {
      const { error } = await supabase.rpc('resolve_status_conflicts', {
        p_estimate_id: estimateId,
        p_conflict_resolution: conflictResolution
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error resolving conflicts:', error);
      throw error;
    }
  };

  // Enhanced version creation following business rules
  // Now supports passing line items directly as override for sent/approved estimates
  const createVersionEnhanced = async (
    estimateId: string,
    changesData?: any,
    reasonForChange?: string,
    changeDescription?: string,
    lineItemsOverride?: any[] // NEW: Pass modified line items for sent/approved estimates
  ): Promise<VersionCreationResult> => {
    setIsLoading(true);
    
    try {
      console.log('[useEstimateVersioningEnhanced] Creating version', {
        estimateId,
        hasLineItemsOverride: !!lineItemsOverride,
        lineItemsCount: lineItemsOverride?.length
      });
      
      const { data, error } = await supabase.rpc('create_estimate_version_enhanced', {
        p_estimate_id: estimateId,
        p_changes_data: changesData || null,
        p_reason_for_change: reasonForChange || null,
        p_change_description: changeDescription || null,
        p_line_items_override: lineItemsOverride || null
      });

      if (error) throw error;
      
      console.log('[useEstimateVersioningEnhanced] Version created:', data);
      return data as unknown as VersionCreationResult;
    } catch (error) {
      console.error('Error creating enhanced version:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Direct status update - bypasses RPC and uses DB triggers for main version recomputation
  const updateVersionStatusDirect = async (
    estimateId: string,
    versionId: string,
    newStatus: EstimateStatus,
    notes?: string
  ): Promise<void> => {
    setIsLoading(true);
    
    try {
      console.log('🔄 Direct status update:', { versionId, newStatus, estimateId });
      
      // Get old main version before update
      const { data: oldEstimate } = await supabase
        .from('estimates')
        .select('main_version_id')
        .eq('id', estimateId)
        .single();

      // Direct update to estimate_versions - triggers will handle status_changed_at and main recomputation
      const { error } = await supabase
        .from('estimate_versions')
        .update({ 
          status: newStatus,
          // Let the trigger handle status_changed_at automatically
        })
        .eq('id', versionId);

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      // Get new main version after update to check if it changed
      const { data: newEstimate } = await supabase
        .from('estimates')
        .select('main_version_id')
        .eq('id', estimateId)
        .single();

      const mainVersionChanged = oldEstimate?.main_version_id !== newEstimate?.main_version_id;
      console.log('📊 Main version change:', {
        old: oldEstimate?.main_version_id,
        new: newEstimate?.main_version_id,
        changed: mainVersionChanged,
        isNowMain: newEstimate?.main_version_id === versionId
      });

      toast.success(`Status updated to ${newStatus}${mainVersionChanged ? ' (main version updated)' : ''}`);
    } catch (error) {
      console.error('Error updating version status:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Legacy RPC-based update (kept for fallback)
  const updateVersionStatus = async (
    estimateId: string,
    versionId: string,
    newStatus: EstimateStatus,
    notes?: string
  ): Promise<void> => {
    setIsLoading(true);
    
    try {
      // Use the new comprehensive function that handles all business rules
      const { data, error } = await supabase.rpc('update_estimate_version_status_with_rules', {
        p_version_id: versionId,
        p_new_status: newStatus,
        p_notes: notes || null
      });

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      // Check if the operation was successful
      const result = data as unknown as StatusUpdateResult;
      // Accept either success === true OR changed === true as success
      const isSuccessful = result.success === true || result.changed === true;
      if (!isSuccessful) {
        throw new Error(result.error || 'Failed to update status');
      }

      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating version status:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh pricing following business rules
  const refreshPricing = async (
    estimateId: string,
    changedComponents?: Array<{ id: string; type: string }>
  ): Promise<PricingRefreshResult> => {
    setIsLoading(true);
    
    try {
      // Get current main version status
      const { data: estimate, error: estimateError } = await supabase
        .from('estimates')
        .select('status, main_version_id')
        .eq('id', estimateId)
        .single();

      if (estimateError) throw estimateError;

      // Check if there are actual pricing changes
      const { data: refreshResult, error: refreshError } = await supabase.rpc('refresh_estimate_prices', {
        p_estimate_id: estimateId
      });

      if (refreshError) throw refreshError;

      // If no new version was created, there were no changes
      if (!refreshResult) {
        return {
          hasChanges: false,
          action: 'no_changes'
        };
      }

      // Based on current status, determine action taken
      const currentStatus = estimate.status as EstimateStatus;
      
      if (currentStatus === 'draft') {
        return {
          hasChanges: true,
          action: 'direct_update'
        };
      } else if (currentStatus === 'sent') {
        return {
          hasChanges: true,
          action: 'new_version',
          version: {
            version_id: refreshResult,
            version_number: 0, // Will be populated by the function
            version_type: 'estimate',
            action: 'created_new_version'
          }
        };
      } else if (currentStatus === 'approved') {
        return {
          hasChanges: true,
          action: 'change_order',
          version: {
            version_id: refreshResult,
            version_number: 0, // Will be populated by the function
            version_type: 'change_order',
            action: 'created_new_version'
          }
        };
      }

      return {
        hasChanges: false,
        action: 'no_changes'
      };
    } catch (error) {
      console.error('Error refreshing pricing:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Send version with new business rules
  const sendVersion = async (versionId: string): Promise<void> => {
    setIsLoading(true);
    
    try {
      // Get version details
      const { data: version, error: versionError } = await supabase
        .from('estimate_versions')
        .select('estimate_id')
        .eq('id', versionId)
        .single();

      if (versionError) throw versionError;

      // Update to sent status using the comprehensive function
      await updateVersionStatus(version.estimate_id, versionId, 'sent');
    } catch (error) {
      console.error('Error sending version:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Get all versions for an estimate
  const getEstimateVersions = async (estimateId: string) => {
    try {
      const { data, error } = await supabase
        .from('estimate_versions')
        .select('*')
        .eq('estimate_id', estimateId)
        .order('version_number', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching estimate versions:', error);
      return [];
    }
  };

  // Manual main version selection  
  const setMainVersionManually = async (
    estimateId: string,
    versionId: string
  ): Promise<{ success: boolean; error?: string; manual_override?: boolean }> => {
    setIsLoading(true);
    
    try {
      // Directly update the estimates table with the new main_version_id
      const { error } = await supabase
        .from('estimates')
        .update({ main_version_id: versionId })
        .eq('id', estimateId);

      if (error) throw error;
      
      return { success: true, manual_override: true };
    } catch (error) {
      console.error('Error setting main version manually:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Clear manual override and return to auto selection
  const clearMainVersionOverride = async (
    estimateId: string
  ): Promise<{ success: boolean; error?: string; cleared_override?: boolean }> => {
    setIsLoading(true);
    
    try {
      // Call our helper function to recompute the main version based on status priority
      const { error } = await supabase.rpc('set_main_version_for_estimate', {
        p_estimate_id: estimateId
      });

      if (error) throw error;
      
      return { success: true, cleared_override: true };
    } catch (error) {
      console.error('Error clearing main version override:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    checkStatusUpgradeConflicts,
    resolveStatusConflicts,
    createVersionEnhanced,
    updateVersionStatus,
    updateVersionStatusDirect,
    refreshPricing,
    sendVersion,
    getEstimateVersions,
    setMainVersionManually,
    clearMainVersionOverride
  };
};